// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { LabelEditorUndoRedoData } from '@shared/undo/operation/label-editor-sequence-operation';
import { BehaviorSubject, map, Observable, skip, Subject } from 'rxjs';
import { AsideLabelEditorData } from '../aside-label-editor/aside-label-editor.component';
import { LabelEditorMode } from '../label-editor.component';

@Injectable({ providedIn: 'root' })
export class LabelEditorStateManager {

  private _labelEditorUpdateStateSubject = new Subject<boolean>()
  private _labelEditorUndoRedoOperationCompletedSubject = new Subject<LabelEditorUndoRedoData>()
  private _directPrintingTableProviderUpdateSubject = new Subject<void>()
  private _asideLabelEditorModeSubject = new BehaviorSubject<LabelEditorMode>(LabelEditorMode.CLOSED);
  private _fullScreenEnabledSubject = new BehaviorSubject<boolean>(false);
  private _editorDataSubject = new BehaviorSubject<AsideLabelEditorData>(null);
  private _openLabelEditorErrorSubject = new Subject<{title:string, message:string}>();

  public get labelEditorMode(): LabelEditorMode {
    return this._asideLabelEditorModeSubject.getValue();
  }

  public get isFullScreenViewSnapshot(): boolean {
    return this._fullScreenEnabledSubject.getValue();
  }

  public get isLabelEditorOpenSnapshot(): boolean {
    return !!(this._editorDataSubject.getValue());
  }

  public get asideLabelEditorMode$(): Observable<LabelEditorMode> {
    return this._asideLabelEditorModeSubject.asObservable().pipe(skip(1));
  }

  public get editorDataChange$(): Observable<AsideLabelEditorData>{
    return this._editorDataSubject.asObservable().pipe(skip(1));
  }

  public get fullScreenChange$(): Observable<boolean> {
    return this._fullScreenEnabledSubject.asObservable().pipe(skip(1));
  }

  public get isFullScreenView$(): Observable<boolean> {
    return this._fullScreenEnabledSubject.asObservable();
  }

  public get isLabelEditorOpen$(): Observable<boolean> {
    return this._editorDataSubject.pipe(map((data) => !!data));
  }

  public get labelEditorUpdateState$(): Observable<boolean> {
    return this._labelEditorUpdateStateSubject.asObservable();
  }

  public get labelEditorUndoRedoOperationCompleted$(): Observable<LabelEditorUndoRedoData> {
    return this._labelEditorUndoRedoOperationCompletedSubject.asObservable();
  }

  public get directPrintingTableProviderUpdate$(): Observable<void> {
    return this._directPrintingTableProviderUpdateSubject.asObservable();
  }

  public get openLabelEditorErrorReported$(): Observable<{title:string, message:string}> {
    return this._openLabelEditorErrorSubject.asObservable();
  }

  public changeLabelEditorMode(mode: LabelEditorMode): void {
    this._asideLabelEditorModeSubject.next(mode);
  }

  public closeAsideLabelEditor(): void {
    this._editorDataSubject.next(null);
  }

  public openAsideLabelEditor(data: AsideLabelEditorData): void {
    this._editorDataSubject.next(data);
  }

  public toggleFullScreenView(): void {
    this._fullScreenEnabledSubject.next(!this._fullScreenEnabledSubject.value);
  }

  public setLabelEditorUpdateState(isEnabled: boolean): void {
    this._labelEditorUpdateStateSubject.next(isEnabled)
  }

  public notifyLabelEditorUndoRedoOperationCompleted(data: LabelEditorUndoRedoData): void {
    this._labelEditorUndoRedoOperationCompletedSubject.next(data)
  }

  public notifyUpdateDirectPrintingTableProvider(): void {
    this._directPrintingTableProviderUpdateSubject.next()
  }

  public reportOpenLabelEditorError(data: { title: string, message: string }) {
    this._openLabelEditorErrorSubject.next(data);
  }
}
